import { isListaDao } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Lista brokered fixed-term markets (Moolah + LendingBroker).
 *
 * Only applies to rows that actually carry a broker rate card: a plain Moolah
 * market is an ordinary variable pool and must keep the generic sheet, so the
 * adapter checks for the brokered signal rather than the lender key alone.
 *
 * Distinctive: the debt ACCRUES linearly and then FREEZES at maturity, and
 * early repayment costs roughly half the remaining-term interest.
 */
export const listaBrokerAdapter: TermAdapter = {
  id: 'lista-broker',
  matches: isListaDao,
  profileId: (input) =>
    input.variableBorrowDisabled && input.terms?.length
      ? 'lista.broker@v1'
      : 'pool.variable@v1',
  build: (input) => {
    const brokered =
      input.variableBorrowDisabled === true && !!input.terms?.length
    if (!brokered) return {}
    return {
      borrow: {
        debtShape: 'accruing',
        exit: {
          earlyRepay: 'penalty',
          atMaturityCost: 'accrued',
          // A keeper refinances an unpaid loan into the variable position
          // rather than liquidating it.
          lateBehaviour: 'refinanced',
          partialAllowed: true,
        },
        maturity: { atMaturity: 'refinanced' },
        liquidation: { trigger: 'price', seizure: 'proportional' },
        counterparty: {
          kind: 'broker',
          address: (input.market?.broker as string) ?? undefined,
          solvency: 'overcollateralized',
        },
      },
      constraints: {
        positionModel: 'loan-id',
        positionIdMeaning:
          'The broker posId; the dynamic (flexible) loan uses the uint128 max sentinel.',
      },
    }
  },
}
